import { useEffect } from 'react'
import { Capacitor } from '@capacitor/core'
import { Keyboard } from '@capacitor/keyboard'

const KEYBOARD_HEIGHT_VAR = '--app-keyboard-height'
const KEYBOARD_VISIBLE_CLASS = 'app-keyboard-visible'
const MIN_KEYBOARD_HEIGHT = 80

const setKeyboardHeight = (height: number) => {
	const safeHeight = Math.max(0, Math.round(height))

	document.documentElement.style.setProperty(KEYBOARD_HEIGHT_VAR, `${safeHeight}px`)
	document.documentElement.classList.toggle(KEYBOARD_VISIBLE_CLASS, safeHeight > 0)
}

const getVisualViewportKeyboardHeight = () => {
	const viewport = window.visualViewport

	if (!viewport) {
		return 0
	}

	return Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop)
}

export const KeyboardViewportBridge = () => {
	useEffect(() => {
		let baseWindowHeight = window.innerHeight
		let rafId: number | null = null
		let resetTimer: number | null = null
		let keyboardWillShowListener: { remove: () => Promise<void> } | undefined
		let keyboardDidShowListener: { remove: () => Promise<void> } | undefined
		let keyboardWillHideListener: { remove: () => Promise<void> } | undefined
		let keyboardDidHideListener: { remove: () => Promise<void> } | undefined
		let isMounted = true

		const addKeyboardListener = (
			eventName: 'keyboardWillShow' | 'keyboardDidShow',
			listener: (info: { keyboardHeight: number }) => void,
			setHandle: (handle: { remove: () => Promise<void> }) => void,
		) => {
			void Keyboard.addListener(eventName, listener)
				.then(handle => {
					if (isMounted) {
						setHandle(handle)
					} else {
						void handle.remove()
					}
				})
				.catch(() => {})
		}

		const addKeyboardHideListener = (
			eventName: 'keyboardWillHide' | 'keyboardDidHide',
			setHandle: (handle: { remove: () => Promise<void> }) => void,
		) => {
			void Keyboard.addListener(eventName, resetKeyboardHeight)
				.then(handle => {
					if (isMounted) {
						setHandle(handle)
					} else {
						void handle.remove()
					}
				})
				.catch(() => {})
		}

		const applyVisualViewportHeight = () => {
			const height = getVisualViewportKeyboardHeight()
			setKeyboardHeight(height >= MIN_KEYBOARD_HEIGHT ? height : 0)
		}

		const applyNativeKeyboardHeight = (height: number) => {
			if (rafId !== null) {
				window.cancelAnimationFrame(rafId)
			}

			rafId = window.requestAnimationFrame(() => {
				const visualViewportHeight = getVisualViewportKeyboardHeight()

				if (visualViewportHeight >= MIN_KEYBOARD_HEIGHT) {
					setKeyboardHeight(visualViewportHeight)
					return
				}

				const resizedByNativeWebView = baseWindowHeight - window.innerHeight >= MIN_KEYBOARD_HEIGHT
				setKeyboardHeight(resizedByNativeWebView ? 0 : height)
			})
		}

		const resetKeyboardHeight = () => {
			setKeyboardHeight(0)

			if (resetTimer !== null) {
				window.clearTimeout(resetTimer)
			}

			resetTimer = window.setTimeout(() => {
				baseWindowHeight = window.innerHeight
			}, 180)
		}

		const visualViewport = window.visualViewport
		visualViewport?.addEventListener('resize', applyVisualViewportHeight)
		visualViewport?.addEventListener('scroll', applyVisualViewportHeight)
		window.addEventListener('orientationchange', resetKeyboardHeight)

		if (Capacitor.isNativePlatform()) {
			addKeyboardListener(
				'keyboardWillShow',
				info => applyNativeKeyboardHeight(info.keyboardHeight),
				listener => {
					keyboardWillShowListener = listener
				},
			)
			addKeyboardListener(
				'keyboardDidShow',
				info => applyNativeKeyboardHeight(info.keyboardHeight),
				listener => {
					keyboardDidShowListener = listener
				},
			)
			addKeyboardHideListener('keyboardWillHide', listener => {
				keyboardWillHideListener = listener
			})
			addKeyboardHideListener('keyboardDidHide', listener => {
				keyboardDidHideListener = listener
			})
		} else {
			applyVisualViewportHeight()
		}

		return () => {
			isMounted = false
			visualViewport?.removeEventListener('resize', applyVisualViewportHeight)
			visualViewport?.removeEventListener('scroll', applyVisualViewportHeight)
			window.removeEventListener('orientationchange', resetKeyboardHeight)

			if (rafId !== null) {
				window.cancelAnimationFrame(rafId)
			}

			if (resetTimer !== null) {
				window.clearTimeout(resetTimer)
			}

			void keyboardWillShowListener?.remove()
			void keyboardDidShowListener?.remove()
			void keyboardWillHideListener?.remove()
			void keyboardDidHideListener?.remove()
		}
	}, [])

	return null
}
